import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { ImageGenerateRequest, ImagePrompt, SuggestionRequestStatus, SuggestionRequestWithRequester } from 'types'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { AIRateLimitResponseError, AIInsufficientCreditsError } from 'api/clients/AiClient'

interface Props {
  workspaceId: number
}

export interface Data {
  request: ImageGenerateRequest
}

export interface Variables {
  prompt: Omit<ImagePrompt, 'generation_type'> & { priority: string }
  signal?: AbortSignal
  model?: 'mystic' | 'openai'
}

export type Options = Omit<UseMutationOptions<Data, Error, Variables>, 'mutationFn'>

const generateImage = async ({ workspaceId, prompt, signal, model }: Variables & Props) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/generate_image`, {
    method: 'POST',
    body: {
      model,
      prompt: {
        ...prompt,
        generation_type: GenerationType.Image,
      },
    },
    signal,
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    if (response.type === 'error' && response.status === 429) {
      throw new AIRateLimitResponseError(
        'You have exceeded the number of allowed image generations per day. Please try again tomorrow.',
      )
    }
    if (response.type === 'error' && response.status === 402) {
      throw new AIInsufficientCreditsError(
        response.data.error || buildErrorMessage(response.data.errors) || 'Insufficient credits for AI image generation.',
      )
    }
    throw new Error(
      response.data.error ||
        buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }

  return response.data
}

export const useGenerateImage = ({ workspaceId }: Props, options?: Options) => {
  return useMutation<Data, Error, Variables>((variables) => generateImage({ workspaceId, ...variables }), options)
}

export const useGenerateImageSync = (
  { workspaceId }: Props,
  options?: Omit<UseMutationOptions<{ request: SuggestionRequestWithRequester }, Error, Variables>, 'mutationFn'>,
) =>
  useMutation<{ request: SuggestionRequestWithRequester }, Error, Variables>(async (variables) => {
    let generationResponse: { request: SuggestionRequestWithRequester }
    let interval = 500

    const { request } = await generateImage({ workspaceId, ...variables })

    do {
      const res = await sendToApi<{ request: SuggestionRequestWithRequester }>(`/ai_suggestion_requests/${request.id}`)
      const signal = variables.signal

      // need to throw an error to stop the mutation, aborting the polling request would leave the mutation running
      if (signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError')
      }

      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }
      generationResponse = res.data
      if (
        [SuggestionRequestStatus.Expired, SuggestionRequestStatus.Failed].includes(generationResponse?.request.status)
      ) {
        throw new Error('image generation failed')
      }
      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (
      ![
        SuggestionRequestStatus.Successful,
        SuggestionRequestStatus.Failed,
        SuggestionRequestStatus.Aborted,
        SuggestionRequestStatus.Expired,
      ].includes(generationResponse?.request.status)
    )

    return generationResponse
  }, options)
